import {listPopups} from './api';
import {hiddenPopupIds} from './popup-hidden';
import {openPopupWindow, pageLanguage} from './render';

// The most closed popups the request names: the popups API (INAPP_POPUPS_MAX_EXCLUDED) refuses more than 50.
const MAX_EXCLUDED = 50;

// The ids of the popups closed in this browser that the request may name, the smallest first. The server refuses an id
// above 2**63 - 1 (MAX_ID, notifications/api_views.py), and then answers 400 for the whole request: no popup would open
// in this browser. A safe integer is exact and far below that limit, so a key that is no id the server could hold (a
// stored entry that was tampered with, or written by another script) is never sent.
function closedIdsToSend(hidden) {
    const sendable = [...hidden].filter((id) => Number.isSafeInteger(id));
    return sendable.sort((left, right) => left - right).slice(0, MAX_EXCLUDED);
}

// The body classes that mark a screen in the way: a window opening or open (Bootstrap marks the body as soon as a
// modal starts to show), the bell's panel, and the bots guide (it marks the body when it starts, a moment before
// driver.js adds its own classes, src/bots/js/guide.js).
const BUSY_BODY_CLASSES = ['modal-open', 'inapp-bell-open', 'bot-guide-active'];

// Another screen is in the way: a window or an offcanvas is open, a driver.js guide runs (the bots page starts one
// for new users), or one of the body classes above is set.
function screenIsBusy() {
    return Boolean(document.querySelector('.modal.show, .offcanvas.show, .driver-active, .driver-overlay'))
        || BUSY_BODY_CLASSES.some((name) => document.body.classList.contains(name));
}

// Opens the oldest unread popup announcement, at most one per page load (docs/plans/site-announcements-design.md,
// "Frontend"). firstCount is a promise of the bell's first unread count (bell.js): it resolves to null when the
// request failed or the user is signed out, and is undefined on a page without a bell, where nothing happens.
export function initInAppPopups(firstCount, i18nReady = Promise.resolve()) {
    if (!firstCount || document.body.dataset.inappPopups === 'off') {
        return;
    }
    Promise.all([firstCount, i18nReady.catch(() => null)])
        .then(async ([count]) => {
            if (!(count > 0)) {
                return;
            }
            // The popups closed in this browser go with the request, and the server leaves them out before its limit
            // of 5: otherwise five closed ones would fill the answer and keep every newer popup from opening.
            const hidden = hiddenPopupIds();
            const data = await listPopups(pageLanguage(), closedIdsToSend(hidden));
            const item = (data?.items || []).find((entry) => entry?.details && !hidden.has(Number(entry.id)));
            if (!item || screenIsBusy()) {
                return;
            }
            const {openInAppPopup} = await import(/* webpackChunkName: "inapp-popup" */ './popup');
            // Another window may have opened while the list or the window's code was loading.
            if (!screenIsBusy()) {
                openInAppPopup(item, {mode: 'popup'});
            }
        })
        .catch(() => {
            // A failed request or chunk leaves the page as it is; the next page tries again.
        });
}

// The staff preview page (templates/notifications/popup_preview.html): the window of the embedded broadcast, whose
// buttons only close it; "Show again" opens it again. Nothing is read or stored.
export function initInAppPopupPreview(i18nReady = Promise.resolve()) {
    // Only the <script type="application/json"> that json_script writes holds the data. Any other element with this id
    // is plain markup, and the window writes the body of what it reads as markup: it is ignored, whatever its text.
    const data = document.querySelector('script[type="application/json"]#inapp-popup-preview-data');
    const again = document.querySelector('[data-inapp-popup-preview-again]');
    if (!data || !again) {
        return;
    }
    let item;
    try {
        item = JSON.parse(data.textContent);
    } catch (error) {
        return;
    }
    const show = () => openPopupWindow(item, {mode: 'preview', returnFocus: again}).catch(() => {});
    again.addEventListener('click', show);
    i18nReady.catch(() => null).then(show);
}
